import React, { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { useAuth } from '../context/AuthContext'
import { APP_USER_ROLES } from '../lib/config/status-config'
import { TASK_STATUS_LABELS } from '../lib/config/project-config'
import { mutations } from '../lib/supabase/mutations'
import { queries } from '../lib/supabase/queries'
import { Profile, RelatedAppRecord } from '../lib/types/database.types'

type RelatedAppDraft = {
  name: string
  next_release_version: string
  responsible_person: string
  default_publish_release_notes: boolean
}

type UserDraft = Pick<
  Profile,
  | 'default_rate'
  | 'role'
  | 'quote_first'
  | 'is_tester'
  | 'is_2nd_tester'
  | 'is_coder'
  | 'is_task_approver'
  | 'is_finance_approver'
  | 'is_uat_release_admin'
  | 'is_prod_release_admin'
>

type StatusDefaultDaysDraft = Record<string, string>

const STATUS_DEFAULT_GROUPS = [
  { label: 'Approval', statuses: ['pending_quote', 'pending_finance_approval'] },
  { label: 'In Development', statuses: ['in_dev_progress'] },
  { label: 'Testing', statuses: ['in_dev_testing', 'in_uat_testing'] },
  { label: 'Release', statuses: ['ready_for_uat', 'ready_for_prod'] },
  { label: 'Other', statuses: ['on_hold', 'completed'] },
] as const

const inputClassName = 'w-full rounded-lg border border-gray-300 bg-stone-50 px-3 py-2 text-sm text-gray-800'
const selectClassName = 'w-full rounded-lg border border-gray-300 bg-stone-50 px-3 py-2 text-sm text-gray-800'

const createRelatedAppDraft = (app: RelatedAppRecord): RelatedAppDraft => ({
  name: app.name,
  next_release_version: app.next_release_version || '',
  responsible_person: app.responsible_person || '',
  default_publish_release_notes: app.default_publish_release_notes,
})

const createUserDraft = (profile: Profile): UserDraft => ({
  default_rate: profile.default_rate,
  role: profile.role,
  quote_first: profile.quote_first,
  is_tester: profile.is_tester,
  is_2nd_tester: profile.is_2nd_tester,
  is_coder: profile.is_coder,
  is_task_approver: profile.is_task_approver,
  is_finance_approver: profile.is_finance_approver,
  is_uat_release_admin: profile.is_uat_release_admin,
  is_prod_release_admin: profile.is_prod_release_admin,
})

export const ManagePage: React.FC = () => {
  const queryClient = useQueryClient()
  const { profile: currentProfile } = useAuth()
  const [relatedAppDrafts, setRelatedAppDrafts] = useState<Record<string, RelatedAppDraft>>({})
  const [userDrafts, setUserDrafts] = useState<Record<string, UserDraft>>({})
  const [newAppName, setNewAppName] = useState('')
  const [newAppNextRelease, setNewAppNextRelease] = useState('')
  const [newAppResponsiblePerson, setNewAppResponsiblePerson] = useState('')
  const [newAppDefaultPublishReleaseNotes, setNewAppDefaultPublishReleaseNotes] = useState(true)
  const [relatedAppsError, setRelatedAppsError] = useState<string | null>(null)
  const [usersError, setUsersError] = useState<string | null>(null)
  const [activeRelatedAppId, setActiveRelatedAppId] = useState<string | null>(null)
  const [activeUserId, setActiveUserId] = useState<string | null>(null)
  const [statusDefaultDaysDrafts, setStatusDefaultDaysDrafts] = useState<StatusDefaultDaysDraft>({})
  const [statusDefaultsError, setStatusDefaultsError] = useState<string | null>(null)

  const relatedAppsQuery = useQuery({
    queryKey: ['related-apps'],
    queryFn: queries.getRelatedApps,
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  const statusDefaultsQuery = useQuery({
    queryKey: ['task-status-default-days'],
    queryFn: queries.getTaskStatusDefaultDays,
  })

  useEffect(() => {
    if (!relatedAppsQuery.data) {
      return
    }

    setRelatedAppDrafts(
      Object.fromEntries(relatedAppsQuery.data.map((app) => [app.id, createRelatedAppDraft(app)]))
    )
  }, [relatedAppsQuery.data])

  useEffect(() => {
    if (!profilesQuery.data) {
      return
    }

    setUserDrafts(
      Object.fromEntries(profilesQuery.data.map((item) => [item.id, createUserDraft(item)]))
    )
  }, [profilesQuery.data])

  useEffect(() => {
    if (!statusDefaultsQuery.data) {
      return
    }

    setStatusDefaultDaysDrafts(
      Object.fromEntries(statusDefaultsQuery.data.map((item) => [item.status, String(item.default_days)]))
    )
  }, [statusDefaultsQuery.data])

  const invalidateManageQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['related-apps'] }),
      queryClient.invalidateQueries({ queryKey: ['profiles'] }),
      queryClient.invalidateQueries({ queryKey: ['tasks'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      queryClient.invalidateQueries({ queryKey: ['task-status-default-days'] }),
    ])
  }

  const createRelatedAppMutation = useMutation({
    mutationFn: mutations.createRelatedApp,
    onSuccess: async () => {
      await invalidateManageQueries()
      setNewAppName('')
      setNewAppNextRelease('')
      setNewAppResponsiblePerson('')
      setNewAppDefaultPublishReleaseNotes(true)
      setRelatedAppsError(null)
    },
  })

  const updateRelatedAppMutation = useMutation({
    mutationFn: ({ appId, updates }: { appId: string; updates: Parameters<typeof mutations.updateRelatedApp>[1] }) =>
      mutations.updateRelatedApp(appId, updates),
    onSuccess: async () => {
      await invalidateManageQueries()
      setRelatedAppsError(null)
    },
  })

  const updateProfileMutation = useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: Parameters<typeof mutations.updateProfile>[1] }) =>
      mutations.updateProfile(userId, updates),
    onSuccess: async () => {
      await invalidateManageQueries()
      setUsersError(null)
    },
  })

  const updateTaskStatusDefaultDaysMutation = useMutation({
    mutationFn: (updates: Array<{ status: string; default_days: number }>) =>
      Promise.all(updates.map((update) => mutations.updateTaskStatusDefaultDays(update.status, { default_days: update.default_days }))),
    onSuccess: invalidateManageQueries,
  })

  const profileOptions = useMemo(
    () => (profilesQuery.data || []).map((item) => ({
      id: item.id,
      label: item.full_name || item.email || 'Unknown user',
      email: item.email || '',
    })),
    [profilesQuery.data]
  )

  if (currentProfile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }

  const isLoading = relatedAppsQuery.isLoading || profilesQuery.isLoading || statusDefaultsQuery.isLoading
  const error = relatedAppsQuery.error || profilesQuery.error || statusDefaultsQuery.error
  const statusDefaultGroups = useMemo(() => {
    const rows = statusDefaultsQuery.data || []
    const knownStatuses = new Set(STATUS_DEFAULT_GROUPS.flatMap((group) => group.statuses))
    const groups = STATUS_DEFAULT_GROUPS.map((group) => ({
      label: group.label,
      rows: rows.filter((row) => group.statuses.includes(row.status as never)),
    }))
    const unknownRows = rows.filter((row) => !knownStatuses.has(row.status as never))

    return unknownRows.length > 0
      ? [...groups, { label: 'Unknown', rows: unknownRows }]
      : groups
  }, [statusDefaultsQuery.data])

  const handleCreateRelatedApp = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!newAppName.trim()) {
      setRelatedAppsError('App name is required')
      return
    }

    try {
      await createRelatedAppMutation.mutateAsync({
        name: newAppName.trim(),
        current_version: null,
        next_release_version: newAppNextRelease.trim() || null,
        responsible_person: newAppResponsiblePerson || null,
        default_publish_release_notes: newAppDefaultPublishReleaseNotes,
      })
    } catch (err) {
      setRelatedAppsError(err instanceof Error ? err.message : 'Unable to create related app')
    }
  }

  const handleRelatedAppDraftChange = <K extends keyof RelatedAppDraft>(
    appId: string,
    field: K,
    value: RelatedAppDraft[K]
  ) => {
    setRelatedAppDrafts((current) => ({
      ...current,
      [appId]: {
        ...current[appId],
        [field]: value,
      },
    }))
  }

  const handleSaveRelatedApp = async (appId: string) => {
    const draft = relatedAppDrafts[appId]
    if (!draft?.name.trim()) {
      setRelatedAppsError('App name is required')
      return
    }

    setActiveRelatedAppId(appId)

    try {
      await updateRelatedAppMutation.mutateAsync({
        appId,
        updates: {
          name: draft.name.trim(),
          next_release_version: draft.next_release_version.trim() || null,
          responsible_person: draft.responsible_person || null,
          default_publish_release_notes: draft.default_publish_release_notes,
        },
      })
    } catch (err) {
      setRelatedAppsError(err instanceof Error ? err.message : 'Unable to update related app')
    } finally {
      setActiveRelatedAppId(null)
    }
  }

  const handleUserDraftChange = <K extends keyof UserDraft>(userId: string, field: K, value: UserDraft[K]) => {
    setUserDrafts((current) => ({
      ...current,
      [userId]: {
        ...current[userId],
        [field]: value,
      },
    }))
  }

  const handleSaveUser = async (userId: string) => {
    const draft = userDrafts[userId]
    if (!draft) {
      return
    }

    setActiveUserId(userId)

    try {
      await updateProfileMutation.mutateAsync({
        userId,
        updates: draft,
      })
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Unable to update user')
    } finally {
      setActiveUserId(null)
    }
  }

  const handleStatusDefaultDaysChange = (status: string, value: string) => {
    setStatusDefaultDaysDrafts((current) => ({ ...current, [status]: value }))
  }

  const handleSaveStatusDefaultDays = async () => {
    const updates = (statusDefaultsQuery.data || []).map((statusDefault) => ({
      status: statusDefault.status,
      default_days: Number(statusDefaultDaysDrafts[statusDefault.status]),
    }))
    if (updates.some((update) => !Number.isInteger(update.default_days) || update.default_days < 0)) {
      setStatusDefaultsError('Default days must be a whole number of 0 or greater.')
      return
    }

    try {
      await updateTaskStatusDefaultDaysMutation.mutateAsync(updates)
      setStatusDefaultsError(null)
    } catch (err) {
      setStatusDefaultsError(err instanceof Error ? err.message : 'Unable to update status default days')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage</h1>
          <p className="text-sm text-gray-600">Admin tools for related apps, release planning, and user capabilities.</p>
        </div>
      </div>

      {isLoading && (
        <Card>
          <div className="flex min-h-[240px] items-center justify-center">
            <LoadingSpinner text="Loading management data..." />
          </div>
        </Card>
      )}

      {error && !isLoading && (
        <Card>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Failed to load admin data.'}
          </div>
        </Card>
      )}

      {!isLoading && !error && (
        <>
          <Card
            header={
              <div>
                <h2 className="font-semibold text-gray-900">Manage Related Apps</h2>
                <p className="text-sm text-gray-500">Add apps, assign a responsible person, and set the next release version.</p>
              </div>
            }
          >
            <div className="space-y-6">
              <form className="grid gap-4 rounded-xl border border-stone-300 bg-stone-50 p-4 md:grid-cols-5" onSubmit={handleCreateRelatedApp}>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">App Name</label>
                  <input className={inputClassName} value={newAppName} onChange={(event) => setNewAppName(event.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Next Release</label>
                  <input
                    className={inputClassName}
                    value={newAppNextRelease}
                    onChange={(event) => setNewAppNextRelease(event.target.value)}
                    placeholder="vNext"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Responsible Person</label>
                  <select
                    className={selectClassName}
                    value={newAppResponsiblePerson}
                    onChange={(event) => setNewAppResponsiblePerson(event.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {profileOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-3 rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={newAppDefaultPublishReleaseNotes}
                    onChange={(event) => setNewAppDefaultPublishReleaseNotes(event.target.checked)}
                  />
                  <span>Default Publish Release Notes</span>
                </label>
                <div className="md:col-span-5 flex items-center justify-between gap-3">
                  {relatedAppsError ? <p className="text-sm text-red-700">{relatedAppsError}</p> : <span />}
                  <Button type="submit" isLoading={createRelatedAppMutation.isPending}>Add Related App</Button>
                </div>
              </form>

              <div className="space-y-4">
                {(relatedAppsQuery.data || []).map((app) => {
                  const draft = relatedAppDrafts[app.id] || createRelatedAppDraft(app)
                  const responsiblePersonLabel = profileOptions.find((option) => option.id === app.responsible_person)?.label || 'Unassigned'

                  return (
                    <div key={app.id} className="grid gap-4 rounded-xl border border-stone-300 bg-stone-50 p-4 md:grid-cols-6">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">App Name</label>
                        <input
                          className={inputClassName}
                          value={draft.name}
                          onChange={(event) => handleRelatedAppDraftChange(app.id, 'name', event.target.value)}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Current Release</label>
                        <div className="rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-sm text-gray-700">
                          {app.current_version || 'Not set'}
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Next Release</label>
                        <input
                          className={inputClassName}
                          value={draft.next_release_version}
                          onChange={(event) => handleRelatedAppDraftChange(app.id, 'next_release_version', event.target.value)}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Responsible Person</label>
                        <select
                          className={selectClassName}
                          value={draft.responsible_person}
                          onChange={(event) => handleRelatedAppDraftChange(app.id, 'responsible_person', event.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {profileOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">Current: {responsiblePersonLabel}</p>
                      </div>
                      <label className="flex items-center gap-3 rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={draft.default_publish_release_notes}
                          onChange={(event) => handleRelatedAppDraftChange(app.id, 'default_publish_release_notes', event.target.checked)}
                        />
                        <span>Default Publish Release Notes</span>
                      </label>
                      <div className="flex items-end justify-end">
                        <Button
                          type="button"
                          isLoading={activeRelatedAppId === app.id && updateRelatedAppMutation.isPending}
                          onClick={() => handleSaveRelatedApp(app.id)}
                        >
                          Save App
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Card>

          <Card
            header={
              <div>
                <h2 className="font-semibold text-gray-900">Status Default Due Days</h2>
                <p className="text-sm text-gray-500">Configure the automatic due-date offset for each task status.</p>
              </div>
            }
          >
            <div className="space-y-4">
              {statusDefaultsError && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{statusDefaultsError}</div>}
              <div className="overflow-x-auto rounded-xl border border-stone-300">
                <div className="flex items-center justify-end border-b border-stone-200 bg-stone-50 px-4 py-3">
                  <Button
                    type="button"
                    isLoading={updateTaskStatusDefaultDaysMutation.isPending}
                    onClick={handleSaveStatusDefaultDays}
                  >
                    Save All Status Defaults
                  </Button>
                </div>
                <table className="min-w-full divide-y divide-stone-200 text-sm">
                  <thead className="bg-stone-100 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Default due days</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200 bg-white">
                    {statusDefaultGroups.map((group) => (
                      <React.Fragment key={group.label}>
                        <tr className="bg-stone-100">
                          <th colSpan={3} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                            {group.label}
                          </th>
                        </tr>
                        {group.rows.map((statusDefault) => (
                          <tr key={statusDefault.status}>
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {TASK_STATUS_LABELS[statusDefault.status as keyof typeof TASK_STATUS_LABELS] || statusDefault.status}
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                className="w-28 rounded-lg border border-gray-300 bg-stone-50 px-3 py-2 text-sm text-gray-800"
                                value={statusDefaultDaysDrafts[statusDefault.status] ?? String(statusDefault.default_days)}
                                onChange={(event) => handleStatusDefaultDaysChange(statusDefault.status, event.target.value)}
                              />
                            </td>
                            <td className="px-4 py-3" />
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>

          <Card
            header={
              <div>
                <h2 className="font-semibold text-gray-900">Manage Users</h2>
                <p className="text-sm text-gray-500">Update roles and capability flags for admins, developers, and request approvers.</p>
              </div>
            }
          >
            <div className="space-y-4">
              {usersError && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{usersError}</div>}
              {(profilesQuery.data || []).map((userProfile) => {
                const draft = userDrafts[userProfile.id] || createUserDraft(userProfile)

                return (
                  <div key={userProfile.id} className="rounded-xl border border-stone-300 bg-stone-50 p-4 shadow-sm">
                    <div className="mb-4 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{userProfile.full_name || 'Unnamed user'}</h3>
                        <p className="text-sm text-gray-500">{userProfile.email || 'No email available'}</p>
                      </div>
                      <Button
                        type="button"
                        isLoading={activeUserId === userProfile.id && updateProfileMutation.isPending}
                        onClick={() => handleSaveUser(userProfile.id)}
                      >
                        Save User
                      </Button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Default Rate (R/hour)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={inputClassName}
                          value={draft.default_rate}
                          onChange={(event) => handleUserDraftChange(userProfile.id, 'default_rate', Number(event.target.value))}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                        <select
                          className={selectClassName}
                          value={draft.role}
                          onChange={(event) => handleUserDraftChange(userProfile.id, 'role', event.target.value)}
                        >
                          {APP_USER_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </div>

                      {[
                        { key: 'quote_first', label: 'Quote First' },
                        { key: 'is_tester', label: 'Tester' },
                        { key: 'is_2nd_tester', label: '2nd Tester' },
                        { key: 'is_coder', label: 'Coder' },
                        { key: 'is_task_approver', label: 'Task Approver' },
                        { key: 'is_finance_approver', label: 'Finance Approver' },
                        { key: 'is_uat_release_admin', label: 'UAT Release Admin' },
                        { key: 'is_prod_release_admin', label: 'Prod Release Admin' },
                      ].map((field) => (
                        <label key={field.key} className="flex items-center gap-3 rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={Boolean(draft[field.key as keyof UserDraft])}
                            onChange={(event) =>
                              handleUserDraftChange(
                                userProfile.id,
                                field.key as keyof UserDraft,
                                event.target.checked as never
                              )
                            }
                          />
                          <span>{field.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  )
}